/**
 * TryQuiPus — Motor Contable de Partida Doble
 *
 * Garantiza integridad matemática absoluta:
 *   - Todos los cálculos usan decimal.js (cero errores de punto flotante)
 *   - Ningún asiento se persiste si Sum(Débitos) !== Sum(Créditos)
 *   - Error controlado con detalle de la discrepancia
 *
 * Flujo: Transaction → JournalEntry (con líneas Debe/Haber) → Validación → Persistencia
 */

import Decimal from 'decimal.js';
import type { Transaction, Company } from '../types';
import type { BankDraftRow } from '../types/bank';
import { buscarCuenta, validarCuentaPCGE } from '../data/planContable';
import { IGV_RATE, IGV_DIVISOR } from '../constants/limits';

// Configure decimal.js for financial precision
Decimal.set({ precision: 20, rounding: Decimal.ROUND_HALF_UP });

// ═══════════════════════════════════════════════════════════════════
// IDENTITY GUARD — garantiza que sólo se procesan datos de la
// empresa activa. Se sincroniza desde CompanyContext en cada cambio.
// ═══════════════════════════════════════════════════════════════════

/** companyId de la empresa activa en la sesión. null = contexto no inicializado. */
let _activeCompanyId: string | null = null;

/**
 * Registra el companyId activo a nivel de módulo.
 * Llamado por CompanyProvider en mount y en cada cambio de empresa.
 */
export function setActiveCompanyId(companyId: string | null): void {
  _activeCompanyId = companyId;
}

/** Error lanzado cuando los datos no corresponden a la empresa activa. */
export class CompanyMismatchError extends Error {
  constructor(expected: string, received: string, context: string) {
    super(
      `Unauthorized Access: Company Mismatch in [${context}]. ` +
      `Empresa activa: "${expected}", recibida: "${received}". ` +
      `Verifica que no estás mezclando datos de empresas distintas.`,
    );
    this.name = 'CompanyMismatchError';
  }
}

/**
 * Lanza CompanyMismatchError si el companyId del registro no coincide
 * con la empresa activa. No-op si el guard aún no fue inicializado.
 */
function assertCompanyMatch(companyId: string, context: string): void {
  if (_activeCompanyId !== null && companyId !== _activeCompanyId) {
    throw new CompanyMismatchError(_activeCompanyId, companyId, context);
  }
}

// ═══════════════════════════════════════════════════════════════════
// TIPOS
// ═══════════════════════════════════════════════════════════════════

export interface JournalLine {
  cuenta: string;
  nombreCuenta: string;
  debe: Decimal;
  haber: Decimal;
  glosa: string;
}

export interface JournalEntry {
  id: string;
  fecha: string;
  periodo: string;
  glosa: string;
  lines: JournalLine[];
  totalDebe: Decimal;
  totalHaber: Decimal;
  validated: boolean;
  /** Códigos PCGE no encontrados en planContable — advertencia, no bloquea el asiento */
  pcgeWarnings?: string[];
}

export class PartidaDobleError extends Error {
  public readonly totalDebe: string;
  public readonly totalHaber: string;
  public readonly diferencia: string;

  constructor(totalDebe: Decimal, totalHaber: Decimal) {
    const diff = totalDebe.minus(totalHaber).abs();
    super(
      `Partida doble inválida: Debe=${totalDebe.toFixed(2)} ≠ Haber=${totalHaber.toFixed(2)} ` +
      `(diferencia: ${diff.toFixed(2)}). El asiento NO fue persistido.`
    );
    this.name = 'PartidaDobleError';
    this.totalDebe = totalDebe.toFixed(2);
    this.totalHaber = totalHaber.toFixed(2);
    this.diferencia = diff.toFixed(2);
  }
}

// IGV_RATE and IGV_DIVISOR imported from constants/limits above.

// ═══════════════════════════════════════════════════════════════════
// PCGE VALIDATION HELPER
// ═══════════════════════════════════════════════════════════════════

/** Retorna los códigos de cuenta no reconocidos en el catálogo PCGE oficial. */
function collectPcgeWarnings(lines: JournalLine[]): string[] | undefined {
  const invalid = [...new Set(lines.map(l => l.cuenta).filter(c => !validarCuentaPCGE(c)))];
  return invalid.length > 0 ? invalid : undefined;
}

// ═══════════════════════════════════════════════════════════════════
// MATH HELPERS — todo con Decimal, nunca con float nativo
// ═══════════════════════════════════════════════════════════════════

/** Descompone un monto total en Base Imponible + IGV */
export function desgloseIGV(totalInclIGV: Decimal): { base: Decimal; igv: Decimal } {
  const base = totalInclIGV.dividedBy(IGV_DIVISOR).toDecimalPlaces(2);
  const igv = totalInclIGV.minus(base);
  return { base, igv };
}

/** Calcula IGV sobre una base */
export function calcularIGV(base: Decimal): Decimal {
  return base.times(IGV_RATE).toDecimalPlaces(2);
}

// ═══════════════════════════════════════════════════════════════════
// GENERACIÓN DE ASIENTOS
// ═══════════════════════════════════════════════════════════════════

/**
 * Genera un asiento contable de partida doble a partir de una transacción.
 *
 * GASTOS (amount < 0):
 *   Debe: Cuenta de gasto (6x)    → base imponible
 *   Debe: 4011 IGV Crédito Fiscal → IGV
 *   Haber: 42 Ctas x Pagar        → total
 *
 * INGRESOS (amount > 0):
 *   Debe: 12 Ctas x Cobrar        → total
 *   Haber: Cuenta de ingreso (7x) → base imponible
 *   Haber: 4011 IGV Débito Fiscal  → IGV
 */
export function transactionToJournalEntry(
  tx: Transaction,
  company: Company,
): JournalEntry {
  assertCompanyMatch(company.companyId, 'transactionToJournalEntry');
  const total = new Decimal(tx.amount).abs().toDecimalPlaces(2);
  // Use pre-calculated IGV fields from Transaction (set by classifier/OCR)
  const base = new Decimal(tx.baseImponible).toDecimalPlaces(2);
  const igv = new Decimal(tx.igv).toDecimalPlaces(2);
  const isGasto = tx.amount < 0;
  const txPeriodo = (tx as unknown as Record<string, unknown>).periodo as string | undefined;
  // crypto.randomUUID() — antes era `${periodo}-${sequential}`, y varios
  // callers pasaban un `sequential` hardcodeado a 1 (Upload.tsx, saveTransactionEdit),
  // colisionando en el mismo id para toda transacción del mismo período
  // (confirmado: colisión real bajo volumen de beta — auditoría 2026-09-15).
  const id = crypto.randomUUID();

  const cuentaInfo = buscarCuenta(tx.cuenta);
  const nombreCuenta = cuentaInfo?.nombre || tx.nombreCuenta;

  const lines: JournalLine[] = [];

  if (isGasto) {
    // Art. 18 Ley del IGV: boletas (03) y liquidaciones de compra (04)
    // NO generan crédito fiscal IGV — el IGV es costo no recuperable.
    const esBoleta = tx.tipoDocumento === '03' || tx.tipoDocumento === '04';
    if (esBoleta) {
      // Boleta: gasto = total (base + IGV absorbido, sin 4011 CF)
      lines.push({
        cuenta: tx.cuenta,
        nombreCuenta,
        debe: total,
        haber: new Decimal(0),
        glosa: tx.description,
      });
    } else {
      // Factura/NC/ND: Línea 1 — Debe: Cuenta de gasto → Base Imponible
      lines.push({
        cuenta: tx.cuenta,
        nombreCuenta,
        debe: base,
        haber: new Decimal(0),
        glosa: tx.description,
      });
      // Línea 2 — Debe: 4011 IGV Crédito Fiscal → IGV
      if (igv.greaterThan(0)) {
        lines.push({
          cuenta: '4011',
          nombreCuenta: 'IGV - Cuenta Propia',
          debe: igv,
          haber: new Decimal(0),
          glosa: `IGV Crédito Fiscal - ${tx.description}`,
        });
      }
    }
    // Haber: 4212 Proveedores → Importe Total (igual para boletas y facturas)
    lines.push({
      cuenta: '4212',
      nombreCuenta: 'Emitidas',
      debe: new Decimal(0),
      haber: total,
      glosa: tx.description,
    });
  } else {
    // PCGE 2019: 121 = Facturas, boletas y otros comprobantes por cobrar.
    // 122 = Anticipos de clientes (nunca boletas — corregido N-1, auditoría
    // 2026-09-28: 122 estaba mal asignado a boletas, sobreestimando
    // "Anticipos de Clientes" en vez de Cuentas por Cobrar reales).
    // Línea 1 — Debe: 121 Cuentas por Cobrar → Importe Total
    lines.push({
      cuenta: '121',
      nombreCuenta: buscarCuenta('121')?.nombre || 'Facturas por Cobrar',
      debe: total,
      haber: new Decimal(0),
      glosa: tx.description,
    });
    // Línea 2 — Haber: Cuenta de ingreso (70/704) → Base Imponible
    lines.push({
      cuenta: tx.cuenta,
      nombreCuenta,
      debe: new Decimal(0),
      haber: base,
      glosa: tx.description,
    });
    // Línea 3 — Haber: 4011 IGV Débito Fiscal → IGV
    if (igv.greaterThan(0)) {
      lines.push({
        cuenta: '4011',
        nombreCuenta: 'IGV - Cuenta Propia',
        debe: new Decimal(0),
        haber: igv,
        glosa: `IGV Débito Fiscal - ${tx.description}`,
      });
    }
  }

  const totalDebe = lines.reduce((sum, l) => sum.plus(l.debe), new Decimal(0));
  const totalHaber = lines.reduce((sum, l) => sum.plus(l.haber), new Decimal(0));

  const pcgeWarnings = collectPcgeWarnings(lines);
  return {
    id,
    fecha: tx.date,
    periodo: txPeriodo ?? company.periodo,
    glosa: tx.description,
    lines,
    totalDebe,
    totalHaber,
    validated: false,
    ...(pcgeWarnings && { pcgeWarnings }),
  };
}

/**
 * Builds a symmetric 2-line journal entry for a bank statement posting.
 *
 * Does NOT apply IGV desglose — bank amounts are totals.
 *
 * CARGO (payment/outflow):
 *   DR [contraCuenta]  S/ monto     e.g. DR 6361 Energía eléctrica
 *   CR 1041            S/ monto          CR 1041 Cuentas corrientes BCP
 *
 * ABONO (receipt/inflow):
 *   DR 1041            S/ monto          DR 1041 Cuentas corrientes BCP
 *   CR [contraCuenta]  S/ monto     e.g. CR 1201 Facturas por cobrar
 *
 * id scheme: BANK-{crypto.randomUUID()} — el prefijo BANK- se mantiene porque
 * CajaYBancosPle.tsx filtra asientos con `e.id.startsWith('BANK-')`; el sufijo
 * ya no depende de draftId.slice(0,8) (32 bits, colisionable bajo volumen).
 * source: 'auto' — distinguished by BANK- prefix + [BANCO:BCP] glosa prefix.
 */
export function buildBankJournalEntry(params: {
  draftId:      string;
  fecha:        string;
  periodo:      string;
  tipo:         'CARGO' | 'ABONO';
  monto:        Decimal;
  contraCuenta: string;
  contranombre: string;
  glosa:        string;
  companyId:    string;
  /** Nombre del banco para la cuenta 1041 — si no se pasa usa 'Cuenta Corriente' */
  bankName?:    string;
}): JournalEntry {
  const { fecha, periodo, tipo, monto, contraCuenta, contranombre, glosa } = params;
  const nombreBanco = params.bankName || 'Cuenta Corriente';
  const id = `BANK-${crypto.randomUUID()}`;

  const lines: JournalLine[] = tipo === 'CARGO'
    ? [
        { cuenta: contraCuenta, nombreCuenta: contranombre, debe: monto,           haber: new Decimal(0), glosa },
        { cuenta: '1041',       nombreCuenta: nombreBanco,  debe: new Decimal(0), haber: monto,           glosa },
      ]
    : [
        { cuenta: '1041',       nombreCuenta: nombreBanco,  debe: monto,           haber: new Decimal(0), glosa },
        { cuenta: contraCuenta, nombreCuenta: contranombre, debe: new Decimal(0), haber: monto,           glosa },
      ];

  return {
    id,
    fecha,
    periodo,
    glosa,
    lines,
    totalDebe:  monto,
    totalHaber: monto,
    validated:  false,
  };
}

/**
 * Builds a 2-line journal entry that cancels an outstanding invoice (CxP/CxC)
 * against a bank movement matched via PaymentMatchingService.
 *
 * Use this INSTEAD of buildBankJournalEntry when a bank statement line is
 * confirmed as the cancellation of an existing invoice. The cancellation entry
 * closes the receivable/payable directly — gasto/ingreso was already recognized
 * when the invoice was registered, so we do not re-debit a 6x/7x account here.
 *
 * CxP — bank CARGO matched to a purchase invoice (Transaction.amount < 0):
 *   DR 4212 Emitidas — proveedores         monto
 *   CR <bankAccountPcge> <bankAccountLabel> monto
 *
 * CxC — bank ABONO matched to a sales invoice (Transaction.amount > 0):
 *   DR <bankAccountPcge> <bankAccountLabel> monto
 *   CR 121 Cuentas por Cobrar (facturas y boletas) monto
 *
 * The CxC counterparty account MUST mirror the account chosen by
 * transactionToJournalEntry when the invoice was registered (PCGE 2019:
 * facturas y boletas → 121; 122 es Anticipos de Clientes, nunca boletas —
 * corregido N-1, auditoría 2026-09-28). Otherwise the Libro Mayor of the
 * registration account stays open and the cancellation account stays
 * with a credit balance — both wrong.
 *
 * Detracción (SPOT): the bank only transfers (total − detraccionMonto) to the
 * supplier. The function trusts bankTx.cargo / bankTx.abono as the source of
 * truth for the line amount — so a S/2,580 invoice with a S/258 detracción
 * produces a S/2,322 cancellation when the bank shows that exact CARGO.
 *
 * id: PAY-{crypto.randomUUID()} — distinguishes payment-match entries from
 * BANK- (uncatalogued bank moves) and the plain UUIDs of auto invoice JEs.
 */
export function buildPaymentJournalEntry(params: {
  bankTx:           BankDraftRow;
  invoice:          Transaction;
  bankAccountPcge:  string;
  bankAccountLabel: string;
  companyId:        string;
  draftId:          string;
}): JournalEntry {
  const { bankTx, invoice, bankAccountPcge, bankAccountLabel, companyId } = params;

  assertCompanyMatch(companyId, 'buildPaymentJournalEntry');

  const direction      = bankTx.tipo === 'CARGO' ? 'pago' : 'cobro';
  const fecha          = bankTx.fecha;
  const periodo        = fecha.slice(0, 7);          // YYYY-MM (consistent with JE.periodo)

  // Bank-side amount: for SPOT this is already total − detraccionMonto.
  const montoBanco = new Decimal(
    bankTx.tipo === 'CARGO' ? (bankTx.cargo ?? '0') : (bankTx.abono ?? '0'),
  ).abs().toDecimalPlaces(2);

  // CxP: 4212 (matches transactionToJournalEntry for purchases).
  // CxC: 121 — facturas y boletas (matches transactionToJournalEntry for sales;
  // 122 es Anticipos de Clientes, nunca boletas — corregido N-1, auditoría 2026-09-28).
  const contraCuenta = direction === 'pago' ? '4212' : '121';
  const contraNombre = direction === 'pago' ? 'Emitidas' : 'Facturas por Cobrar';

  const docLabel = invoice.serie && invoice.correlativo
    ? `${invoice.serie}-${invoice.correlativo}`
    : '(s/n)';
  const partyName = direction === 'pago'
    ? (invoice.rucProveedor ?? '')
    : (invoice.rucCliente ?? invoice.nombreCliente ?? '');

  const glosa = direction === 'pago'
    ? `Cancelación ${docLabel} — ${partyName} — vía ${bankAccountLabel}`
    : `Cobranza ${docLabel} — ${partyName} — vía ${bankAccountLabel}`;

  const id = `PAY-${crypto.randomUUID()}`;

  const lines: JournalLine[] = direction === 'pago'
    ? [
        { cuenta: contraCuenta,    nombreCuenta: contraNombre,     debe: montoBanco,      haber: new Decimal(0), glosa },
        { cuenta: bankAccountPcge, nombreCuenta: bankAccountLabel, debe: new Decimal(0), haber: montoBanco,      glosa },
      ]
    : [
        { cuenta: bankAccountPcge, nombreCuenta: bankAccountLabel, debe: montoBanco,      haber: new Decimal(0), glosa },
        { cuenta: contraCuenta,    nombreCuenta: contraNombre,     debe: new Decimal(0), haber: montoBanco,      glosa },
      ];

  return {
    id,
    fecha,
    periodo,
    glosa,
    lines,
    totalDebe:  montoBanco,
    totalHaber: montoBanco,
    validated:  false,
  };
}

// ═══════════════════════════════════════════════════════════════════
// AR/AP — PAGOS PARCIALES Y MÚLTIPLES FACTURAS
// ═══════════════════════════════════════════════════════════════════

/**
 * Asiento de pago parcial — usado por AR/AP Reconciliación cuando un banco tx
 * cubre solo parte de una factura, o cuando el saldo residual ≤ S/0.10 se
 * auto-cierra cargando la diferencia a cuenta 679.
 *
 * Caso 1 — Pago parcial normal (esAjusteRedondeo falsy):
 *   CxP: DR 4212 montoPagado  /  CR <banco> montoPagado
 *   CxC: DR <banco> montoPagado  /  CR 121 montoPagado
 *
 * Caso 2 — Ajuste de redondeo (esAjusteRedondeo = true, montoPagado ≤ 0.10):
 *   DR 4212|121  saldoResidual
 *   CR 679       saldoResidual  (Otros gastos financieros)
 *
 * id: PPAY-{crypto.randomUUID()} — distinto de PAY- (cancelación total)
 *     y BANK- (movimiento no catalogado).
 */
export function buildPartialPaymentJournalEntry(input: {
  bankTx:            BankDraftRow;
  invoice:           Transaction;
  montoPagado:       Decimal;
  bankAccountPcge:   string;
  bankAccountLabel:  string;
  companyId:         string;
  draftId:           string;
  esAjusteRedondeo?: boolean;
}): JournalEntry {
  const {
    bankTx, invoice, montoPagado,
    bankAccountPcge, bankAccountLabel,
    companyId, esAjusteRedondeo = false,
  } = input;

  assertCompanyMatch(companyId, 'buildPartialPaymentJournalEntry');

  const monto         = montoPagado.toDecimalPlaces(2);
  const fecha         = bankTx.fecha;
  const periodo       = fecha.slice(0, 7);

  const esCxP     = invoice.amount < 0;
  // 121 — facturas y boletas (122 es Anticipos de Clientes, nunca boletas —
  // corregido N-1, auditoría 2026-09-28).
  const cuentaFactura = esCxP ? '4212' : '121';
  const nombreFactura = esCxP ? 'Emitidas' : 'Facturas por Cobrar';

  const docLabel = invoice.serie && invoice.correlativo
    ? `${invoice.serie}-${invoice.correlativo}`
    : '(s/n)';

  const id = `PPAY-${crypto.randomUUID()}`;

  let lines: JournalLine[];
  let glosa: string;

  if (esAjusteRedondeo) {
    // Caso 2: cierre por redondeo ≤ S/0.10 → cuenta 679
    glosa = `Ajuste redondeo ${docLabel} — S/ ${monto.toFixed(2)} → cta 679`;
    lines = [
      { cuenta: cuentaFactura, nombreCuenta: nombreFactura,              debe: monto,           haber: new Decimal(0), glosa },
      { cuenta: '679',         nombreCuenta: 'Otros gastos financieros', debe: new Decimal(0),  haber: monto,           glosa },
    ];
  } else if (esCxP) {
    // Caso 1 CxP: DR 4212 / CR banco
    glosa = `Pago parcial ${docLabel} — S/ ${monto.toFixed(2)} vía ${bankAccountLabel}`;
    lines = [
      { cuenta: cuentaFactura,   nombreCuenta: nombreFactura,    debe: monto,           haber: new Decimal(0), glosa },
      { cuenta: bankAccountPcge, nombreCuenta: bankAccountLabel, debe: new Decimal(0),  haber: monto,           glosa },
    ];
  } else {
    // Caso 1 CxC: DR banco / CR 121
    glosa = `Cobro parcial ${docLabel} — S/ ${monto.toFixed(2)} vía ${bankAccountLabel}`;
    lines = [
      { cuenta: bankAccountPcge, nombreCuenta: bankAccountLabel, debe: monto,           haber: new Decimal(0), glosa },
      { cuenta: cuentaFactura,   nombreCuenta: nombreFactura,    debe: new Decimal(0),  haber: monto,           glosa },
    ];
  }

  return { id, fecha, periodo, glosa, lines, totalDebe: monto, totalHaber: monto, validated: false };
}

/**
 * Genera un JournalEntry por factura cuando un banco tx cubre múltiples facturas
 * en un solo pago. Cada JE tiene id único con sufijo de posición (1-based).
 *
 * La suma de los montos individuales debe igualar el monto del banco tx —
 * el caller verifica esto con validateMultiMatch() antes de llamar aquí.
 */
export function buildMultiInvoicePayments(input: {
  bankTx:           BankDraftRow;
  invoices:         Array<{ invoice: Transaction; monto: Decimal }>;
  bankAccountPcge:  string;
  bankAccountLabel: string;
  companyId:        string;
  draftId:          string;
}): JournalEntry[] {
  const { bankTx, invoices, bankAccountPcge, bankAccountLabel, companyId, draftId } = input;

  assertCompanyMatch(companyId, 'buildMultiInvoicePayments');

  return invoices.map(({ invoice, monto }, idx) => {
    const je = buildPartialPaymentJournalEntry({
      bankTx, invoice, montoPagado: monto,
      bankAccountPcge, bankAccountLabel,
      companyId, draftId,
      esAjusteRedondeo: false,
    });
    return { ...je, id: `${je.id}-${idx + 1}` };
  });
}

// ═══════════════════════════════════════════════════════════════════
// VALIDACIÓN — la ley de hierro de la partida doble
// ═══════════════════════════════════════════════════════════════════

/**
 * Valida que un asiento cumpla partida doble.
 * ARROJA PartidaDobleError si hay discrepancia.
 */
export function validateEntry(entry: JournalEntry): JournalEntry {
  const totalDebe = entry.lines.reduce(
    (sum, l) => sum.plus(l.debe), new Decimal(0)
  );
  const totalHaber = entry.lines.reduce(
    (sum, l) => sum.plus(l.haber), new Decimal(0)
  );

  if (!totalDebe.equals(totalHaber)) {
    throw new PartidaDobleError(totalDebe, totalHaber);
  }

  return {
    ...entry,
    totalDebe,
    totalHaber,
    validated: true,
  };
}

/**
 * Valida un lote completo de asientos.
 * Retorna los validados + array de errores por los que fallaron.
 */
export function validateBatch(entries: JournalEntry[]): {
  valid: JournalEntry[];
  errors: { entryId: string; error: PartidaDobleError }[];
} {
  const valid: JournalEntry[] = [];
  const errors: { entryId: string; error: PartidaDobleError }[] = [];

  for (const entry of entries) {
    try {
      valid.push(validateEntry(entry));
    } catch (e) {
      if (e instanceof PartidaDobleError) {
        errors.push({ entryId: entry.id, error: e });
      } else {
        throw e;
      }
    }
  }

  return { valid, errors };
}

// ═══════════════════════════════════════════════════════════════════
// PIPELINE COMPLETO: Transactions → Validated Journal Entries
// ═══════════════════════════════════════════════════════════════════

/**
 * Procesa un array de transacciones en asientos contables validados.
 *
 * 1. Genera asientos de partida doble
 * 2. Valida cada uno (Sum(D) === Sum(H))
 * 3. Retorna solo los válidos + reporte de errores
 */
export function processTransactions(
  transactions: Transaction[],
  company: Company,
): {
  entries: JournalEntry[];
  errors: { entryId: string; error: PartidaDobleError }[];
  summary: {
    totalEntries: number;
    validEntries: number;
    failedEntries: number;
    totalDebe: string;
    totalHaber: string;
  };
} {
  assertCompanyMatch(company.companyId, 'processTransactions');
  const rawEntries = transactions.map((tx) =>
    transactionToJournalEntry(tx, company)
  );

  const { valid, errors } = validateBatch(rawEntries);

  const totalDebe = valid.reduce(
    (sum, e) => sum.plus(e.totalDebe), new Decimal(0)
  );
  const totalHaber = valid.reduce(
    (sum, e) => sum.plus(e.totalHaber), new Decimal(0)
  );

  return {
    entries: valid,
    errors,
    summary: {
      totalEntries: rawEntries.length,
      validEntries: valid.length,
      failedEntries: errors.length,
      totalDebe: totalDebe.toFixed(2),
      totalHaber: totalHaber.toFixed(2),
    },
  };
}

// ═══════════════════════════════════════════════════════════════════
// SERIALIZACIÓN — para persistir en IndexedDB/localStorage
// ═══════════════════════════════════════════════════════════════════

export interface SerializedJournalEntry {
  id: string;
  fecha: string;
  periodo: string;
  glosa: string;
  lines: {
    cuenta: string;
    nombreCuenta: string;
    debe: string;
    haber: string;
    glosa: string;
  }[];
  totalDebe: string;
  totalHaber: string;
  validated: boolean;
  /** Propagado desde JournalEntry — códigos PCGE no reconocidos */
  pcgeWarnings?: string[];
}

export function serializeEntry(entry: JournalEntry): SerializedJournalEntry {
  return {
    ...entry,
    lines: entry.lines.map((l) => ({
      ...l,
      debe: l.debe.toFixed(2),
      haber: l.haber.toFixed(2),
    })),
    totalDebe: entry.totalDebe.toFixed(2),
    totalHaber: entry.totalHaber.toFixed(2),
  };
}

export function deserializeEntry(data: SerializedJournalEntry): JournalEntry {
  return {
    ...data,
    lines: data.lines.map((l) => ({
      ...l,
      debe: new Decimal(l.debe),
      haber: new Decimal(l.haber),
    })),
    totalDebe: new Decimal(data.totalDebe),
    totalHaber: new Decimal(data.totalHaber),
  };
}

// ═══════════════════════════════════════════════════════════════════
// PERIOD BALANCE COMPUTATION — Pilar 3 (Gobernanza de Período)
// ═══════════════════════════════════════════════════════════════════

/**
 * Saldo de una cuenta PCGE para un período contable.
 * Computado a partir de los débitos y créditos de los asientos validados.
 */
export interface PeriodBalanceEntry {
  accountCode: string;
  initialBalance: Decimal;
  debits: Decimal;
  credits: Decimal;
  /** finalBalance = initialBalance + debits − credits */
  finalBalance: Decimal;
  /** IDs de todos los asientos que afectan esta cuenta en el período. */
  journalEntryIds: string[];
}

/**
 * Calcula los saldos de período por cuenta PCGE a partir de asientos contables.
 *
 * Función PURA — no accede a la base de datos.
 * Debe invocarse dentro de una transacción Dexie para garantizar atomicidad.
 *
 * @param entries        Asientos del período (ya validados: Σ Debe = Σ Haber).
 * @param initialBalances Mapa accountCode → saldo inicial del período N-1.
 *                        Vacío si es el primer período (saldos iniciales = 0).
 * @returns Mapa accountCode → PeriodBalanceEntry con saldos acumulados.
 */
export function computePeriodBalances(
  entries: (Pick<SerializedJournalEntry, 'id' | 'lines'> & { annulled?: boolean })[],
  initialBalances: Map<string, string> = new Map(),
): Map<string, PeriodBalanceEntry> {
  const result = new Map<string, PeriodBalanceEntry>();

  for (const entry of entries) {
    if (entry.annulled === true) continue; // asientos anulados no suman en balance
    for (const line of entry.lines) {
      const code = line.cuenta;

      if (!result.has(code)) {
        const init = new Decimal(initialBalances.get(code) ?? '0');
        result.set(code, {
          accountCode:     code,
          initialBalance:  init,
          debits:          new Decimal(0),
          credits:         new Decimal(0),
          finalBalance:    init,
          journalEntryIds: [],
        });
      }

      const acc = result.get(code)!;
      acc.debits  = acc.debits.plus(new Decimal(line.debe));
      acc.credits = acc.credits.plus(new Decimal(line.haber));
      // finalBalance = initialBalance + Σ(debe) − Σ(haber)
      acc.finalBalance = acc.initialBalance.plus(acc.debits).minus(acc.credits);

      if (!acc.journalEntryIds.includes(entry.id)) {
        acc.journalEntryIds.push(entry.id);
      }
    }
  }

  return result;
}

/**
 * Extrae el mapa semilla para el siguiente período contable.
 *
 * Modelo mental: las cuentas de balance (1–5) arrastran su saldo neto al
 * período siguiente como `initialBalance`. Las cuentas de resultado (6–7)
 * se excluyen — se cierran a cero al cierre del período y no deben
 * reaparecer como saldos iniciales.
 *
 * Uso típico:
 *   ```
 *   const period1 = computePeriodBalances(entries);
 *   const seed    = extractFinalBalances(period1);   // Map<string, string>
 *   const period2 = computePeriodBalances(entries2, seed);
 *   ```
 *
 * @param balances  Resultado de `computePeriodBalances` del período a cerrar.
 * @returns         `Map<accountCode, finalBalance>` para cuentas 1–5 únicamente.
 *                  Los valores son strings Decimal-safe (`toFixed(2)`).
 */
export function extractFinalBalances(
  balances: Map<string, PeriodBalanceEntry>,
): Map<string, string> {
  const BALANCE_CLASSES = new Set(['1', '2', '3', '4', '5']);
  const result = new Map<string, string>();
  for (const [code, entry] of balances) {
    if (!BALANCE_CLASSES.has(code[0])) continue;
    result.set(code, entry.finalBalance.toFixed(2));
  }
  return result;
}
